import type { Look } from "@squire/core";
import {
  ANIMATIONS,
  BEARD_NAMES,
  COLORS,
  HAIR_COLORS,
  HAIR_COLOR_NAMES,
  HAIR_STYLE_NAMES,
  SHIRT_COLORS,
  SHIRT_NAMES,
  SKIN,
  SKIN_NAMES,
  composeCharacter,
  frameAt,
} from "@squire/sprites";
import { bitmapToSurface, SurfaceCache } from "../render/bitmapCanvas.js";
import { FrameLoop } from "../system/loop.js";
import { clear, h } from "./dom.js";

export interface CreatorResult {
  look: Look;
  companionName: string;
  userName: string;
}

/** Character creator (first run and settings). Resolves when the user confirms. */
export function openCreator(
  root: HTMLElement,
  initial: CreatorResult,
  opts: { title?: string; cancellable?: boolean } = {},
): Promise<CreatorResult | null> {
  return new Promise((resolve) => {
    const look: Look = { ...initial.look };
    const cache = new SurfaceCache(24);
    const preview = h("canvas", { width: 40, height: 44 });
    const ctx = preview.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    const controls = h("div");
    const companion = h("input", {
      type: "text",
      value: initial.companionName,
      maxlength: 24,
      "aria-label": "Imię kompana",
    });
    const user = h("input", {
      type: "text",
      value: initial.userName,
      maxlength: 24,
      placeholder: "np. Wiktor",
      "aria-label": "Twoje imię",
    });

    const swatches = (
      label: string,
      colors: readonly string[],
      names: readonly string[],
      key: "skin" | "hairColor" | "shirt",
    ) =>
      h(
        "div",
        { class: "picker" },
        h("span", { class: "label" }, label),
        h(
          "div",
          { class: "swatches" },
          ...colors.map((c, i) =>
            h("button", {
              style: { background: c },
              title: names[i],
              "aria-label": `${label}: ${names[i]}`,
              "aria-pressed": String(look[key] === i),
              onclick: () => {
                look[key] = i;
                render();
              },
            }),
          ),
        ),
      );

    const stepper = (label: string, names: readonly string[], key: "hair" | "beard") =>
      h(
        "div",
        { class: "picker" },
        h("span", { class: "label" }, label),
        h(
          "button",
          {
            "aria-label": `${label}: poprzednie`,
            onclick: () => ((look[key] = (look[key] + names.length - 1) % names.length), render()),
          },
          "◀",
        ),
        h("span", { class: "value pixel" }, names[look[key]] ?? ""),
        h(
          "button",
          {
            "aria-label": `${label}: następne`,
            onclick: () => ((look[key] = (look[key] + 1) % names.length), render()),
          },
          "▶",
        ),
      );

    const render = () => {
      clear(controls);
      controls.append(
        swatches("Karnacja", SKIN, SKIN_NAMES, "skin"),
        stepper("Fryzura", HAIR_STYLE_NAMES, "hair"),
        swatches("Włosy", HAIR_COLORS, HAIR_COLOR_NAMES, "hairColor"),
        stepper("Zarost", BEARD_NAMES, "beard"),
        h(
          "div",
          { class: "picker" },
          h("span", { class: "label" }, "Okulary"),
          h(
            "button",
            {
              "aria-pressed": String(look.glasses),
              onclick: () => ((look.glasses = !look.glasses), render()),
            },
            look.glasses ? "tak" : "nie",
          ),
        ),
        swatches("Koszulka", SHIRT_COLORS, SHIRT_NAMES, "shirt"),
      );
    };
    render();

    const start = Date.now();
    const loop = new FrameLoop((now) => {
      const anim = ANIMATIONS.idle!;
      const { index, frame } = frameAt(anim, now - start);
      const key = `${JSON.stringify(look)}|${index}`;
      const s = cache.get(key, () => bitmapToSurface(composeCharacter(look, 0, frame.pose)));
      ctx.fillStyle = COLORS.wall;
      ctx.fillRect(0, 0, 40, 44);
      ctx.fillStyle = COLORS.floor;
      ctx.fillRect(0, 38, 40, 6);
      ctx.drawImage(s as CanvasImageSource, -4, -6);
    }, 8);
    loop.start();

    const close = (r: CreatorResult | null) => {
      loop.stop();
      overlay.remove();
      resolve(r);
    };

    const overlay = h(
      "div",
      { class: "overlay" },
      h(
        "div",
        { class: "frame" },
        h("h1", { style: { marginBottom: "0.6em" } }, opts.title ?? "Stwórz swoją postać"),
        h(
          "div",
          { class: "creator" },
          h(
            "div",
            {},
            preview,
            h("p", { style: { fontSize: "0.9rem" } }, "Postać to twoje odbicie. Rośnie razem z tobą."),
          ),
          h(
            "div",
            {},
            controls,
            h(
              "div",
              { class: "grid2", style: { marginTop: "1em" } },
              h("label", { class: "pixel" }, "Kompan"),
              companion,
              h("label", { class: "pixel" }, "Ty"),
              user,
            ),
          ),
        ),
        h(
          "div",
          { style: { display: "flex", gap: "0.8em", justifyContent: "flex-end", marginTop: "1.2em" } },
          opts.cancellable ? h("button", { class: "ghost", onclick: () => close(null) }, "Anuluj") : null,
          h(
            "button",
            {
              class: "primary",
              onclick: () =>
                close({
                  look: { ...look },
                  companionName: companion.value.trim() || "Giermek",
                  userName: user.value.trim(),
                }),
            },
            "Gotowe",
          ),
        ),
      ),
    );
    root.append(overlay);
  });
}
